<script setup>
import { onMounted, reactive, computed, ref } from "vue";
import { datePipe } from "@/utils/datatimeUtils";
import { useRouter } from "vue-router";
import { chatsApi, userApi } from "@/api";
import draggable from "vuedraggable";

const router = useRouter();
const data = reactive({ chats: [] });
const filterKey = reactive({
  searchKey: null,
});
const searchKey = ref("");
const del_id = reactive({ id: 0 });
const canDelete = ref(false); // 削除権限の状態を保持
const isDragging = ref(false); // ドラッグ状態を管理
const canReorder = ref(false); // 並び替え権限（管理者）

const chatCount = computed(() => {
  if (filteredData.value) {
    return filteredData.value.length;
  } else {
    return 0;
  }

});

const filteredData = computed(() => {
  let temp = data.chats;
  if (filterKey.searchKey) {
    temp = temp.filter((row) =>
      Object.keys(row).some(
        (key) =>
          String(row[key]).toLowerCase().indexOf(filterKey.searchKey.toLowerCase()) > -1
      )
    );
  }
  return temp;
});

// 認可チェック関数
const checkAuthorization = async () => {
  try {
    const authResp = await chatsApi.check_authorization();
    console.log('Authorization response:', authResp);
    canDelete.value = authResp.message === "ok";
  } catch (error) {
    console.error('Authorization check error:', error);
    canDelete.value = false;
  }
};

// lifecycle hooks
onMounted(async () => {
  // 認可チェックを実行
  await checkAuthorization();
  // 並び替え権限チェック（管理者のみOK）
  try {
    const reorderAuth = await chatsApi.check_reorder_authorization();
    canReorder.value = reorderAuth.message === "ok";
  } catch (error) {
    canReorder.value = false;
  }
  
  // ユーザー情報を取得してログ出力
  try {
    const userResp = await userApi.whoami();
    console.log('Current user:', userResp?.data);
  } catch (error) {
    console.error('Error getting user info:', error);
  }
  
  await chatsApi.getAll().then(res => {
    console.log('Chats API response:', res?.data);
    data.chats = res?.data;
  });
});

const handleEdit = (chatId) => {
  router.push(`/chats/${chatId}`);
};

const handleCreate = () => {
  router.push(`/chats/new`);
};

const handleFilter = () => {
  filterKey.searchKey = searchKey.value;
};

// Delete the chat
const deleteCurrentChat = async () => {
  try {
    await chatsApi.deleteChat(del_id.id);
    location.reload();
  } catch (error) {
    console.error("Error deleting chat:", error);
  }
};

const setDelId = async (id) => {
  del_id.id = id;
  // 認可がokでない場合は何もしない
  if (!canDelete.value) {
    console.log('Delete operation not allowed');
    return;
  }
  let btn = document.getElementById("showMoalBtn");
  btn.click();
};

// ドラッグ&ドロップのイベントハンドラー
const onDragStart = () => {
  isDragging.value = true;
};

const onDragEnd = () => {
  isDragging.value = false;
};

const onOrderChange = (event) => {
  // 並び替えが完了した時の処理
  console.log('Order changed:', event);
  // ここで必要に応じてサーバーに新しい順序を保存
  if (canReorder.value) {
    saveChatOrder();
  } else {
    // 権限がない場合は元の順序に戻す
    chatsApi.getAll().then(res => {
      data.chats = res?.data;
    });
  }
};

// チャットの順序を保存する関数
const saveChatOrder = async () => {
  try {
    // 各チャットの新しい順序を準備
    const updates = data.chats.map((chat, index) => ({
      chat_id: chat.id,
      display_order: index + 1
    }));
    
    // サーバーに順序を保存
    await chatsApi.bulkUpdateDisplayOrders(updates);
    console.log('Chat order updated successfully:', updates);
  } catch (error) {
    console.error('Error saving chat order:', error);
    // エラーが発生した場合は元の順序に戻す
    await chatsApi.getAll().then(res => {
      data.chats = res?.data;
    });
  }
};
</script>

<template>
  <div class="container">
    <div class="row">
      <h4>{{ $t('ChatListView.h4') }}</h4>
    </div>
    <div class="row">
      <div class="column">
        <label for="serach-keyword">{{ $t('ChatListView.serach_keyword') }}</label>
        <input type="text" id="search-keyword" v-model="searchKey" />
      </div>
    </div>
    <div class="row">
      <div class="column">
        <label for="chat-count">{{ $t('ChatListView.chat_count') }}</label>
        <span id="chat-count">{{ chatCount }}</span>
      </div>
      <div class="column" style="text-align: right">
        <button class="btn btn-primary" @click="handleCreate" style="padding: 0 50px; margin: 10px;">{{
          $t('ChatListView.handle_create') }}</button>
        <button class="btn btn-primary" @click="handleFilter" style="padding: 0 50px; margin: 10px;">{{
          $t('ChatListView.handle_filter') }}</button>
      </div>
    </div>

    <!-- チャットボット一覧テーブル -->
    <table class="table table-hover">
      <thead>
        <tr>
          <th scope="col" style="width: 30px;">&nbsp;&nbsp;&nbsp;</th>
          <th scope="col">{{ $t('ChatListView.chat_title') }}</th>
          <th scope="col">{{ $t('ChatListView.author') }}</th>
          <th scope="col" v-if="canDelete">{{ $t('ChatListView.operation') }}</th>
        </tr>
      </thead>
      <draggable 
        v-model="data.chats" 
        tag="tbody"
        item-key="id"
        @start="onDragStart"
        @end="onDragEnd"
        @change="onOrderChange"
        :disabled="filterKey.searchKey !== null || !canReorder"
        handle=".drag-handle"
      >
        <template #item="{ element: chat }">
          <tr :class="{ 'dragging': isDragging }">
            <td class="drag-handle" style="cursor: move; text-align: center;">
              <span v-if="!filterKey.searchKey && canReorder">⋮⋮</span>
            </td>
            <td @click="!isDragging && handleEdit(chat.id)">{{ chat.chat_title }}</td>
            <td @click="!isDragging && handleEdit(chat.id)">{{ datePipe(chat.created_at) }}</td>
            <td v-if="canDelete">
              <button class="btn btn-danger btn-sm" @click="setDelId(chat.id)">
                {{ $t('ChatListView.delete') }}
              </button>
            </td>
          </tr>
        </template>
      </draggable>
    </table>
  </div>

  

  <!-- チャット削除警告modal -->
  <button id="showMoalBtn" data-bs-toggle="modal" data-bs-target="#delWarning" style="display:none;"></button>
  <div class="modal fade" id="delWarning" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="exampleModalLabel">{{ $t('ChatListView.modal_title') }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p>{{ $t('ChatListView.warning_msg_1') }}</p>
          <p>{{ $t('ChatListView.warning_msg_2') }}</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{ $t('ChatListView.cancel') }}</button>
          <button type="button" class="btn btn-primary" data-bs-dismiss="modal" @click="deleteCurrentChat()">{{
            $t('ChatListView.delete') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.row {
  display: flex;
  margin: 15px 5px;
}

.column {
  flex: 1;
}

/* ドラッグ&ドロップのスタイル */
.drag-handle {
  user-select: none;
  color: #666;
  font-weight: bold;
}

.drag-handle:hover {
  color: #333;
}

.dragging {
  opacity: 0.5;
}

/* ドラッグ中の行のスタイル */
.sortable-ghost {
  opacity: 0.4;
}

.sortable-chosen {
  background-color: #f8f9fa;
}

/* ドラッグハンドルのホバー効果 */
.drag-handle:hover {
  background-color: #e9ecef;
  border-radius: 3px;
}

/* テーブル行のホバー効果を調整 */
tbody tr:hover {
  background-color: #f8f9fa;
}

tbody tr.dragging:hover {
  background-color: transparent;
}

</style>
